import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import { isNotFoundError } from "@/Helpers/isNotFoundError";
import useQuery from "@/Hooks/useQuery";
import useInterval from "@/Hooks/useInterval";
import { useOnce } from "@/Hooks/useOnce";
import { useTrackingService } from "@/Services/TrackingService";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import type { GetTrackingDetails } from "@freightsimple/generated-dashboard-openapi-client/models/GetTrackingDetails";
import type { ShipmentForTracking } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentForTracking";
import type { TrackingInfo } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingInfo";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { TrackingShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingShipmentState";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthentication } from "@/Slices/authenticationSlice";
import ErrorAnimation from "@/Shared/Icons/ErrorAnimation";
import { trackingResolveRedirectPath } from "./Helpers/trackingResolveRedirectPath";
import { trackingResolveNextStep } from "./Helpers/trackingResolveNextStep";
import { trackingResolveGroup } from "./Helpers/trackingResolveGroup";
import { trackingResolveTrackingId } from "./Helpers/trackingResolveTrackingId";
import { TrackingShipmentsScreen } from "./TrackingShipmentsScreen";
import { TrackingOverlapCard } from "./CustomComponents/Internal/TrackingOverlapCard";
import { useUserService } from "@/Services/UserService";

import {
  customsDocsSectionForShipment,
  DetailsSectionForShipment_,
  DocumentsSectionForShipment_,
  mapForShipment,
  NextStepsForDeliverySectionForShipment_,
  NextStepsSectionForShipment_,
  topBarForShipment,
  TrackingSectionForShipment_,
} from "./Internal/trackingShipmentSections";
interface ShowShipmentProps {
  shipment: ShipmentForTracking;
  onReloadShipment: () => void;
}

function ShowShipment_(props: ShowShipmentProps) {
  const shipment = props.shipment;
  const { trId } = shipment;
  const trackingService = useTrackingService();

  const [trackingInfo, setTrackingInfo] = useState<TrackingInfo | undefined>();

  const { checkAuthenticated } = useAuthentication();
  const navigate = useNavigate();
  const shipmentApi = useShipmentsApi();

  useOnce(async () => {
    const isAuthenticated = await checkAuthenticated();

    if (isAuthenticated) {
      const response = await shipmentApi.swapTrackingIdForShipmentId({
        tId: trId,
      });

      if (response.hasAccess && response.shipmentId) {
        navigate(`/view-shipment?shipmentId=${response.shipmentId}`);
        return;
      }
    }

    loadTracking();
  });

  async function loadTracking() {
    const ti = await trackingService.getTracking(trId);

    setTrackingInfo(ti);
  }

  function showCustomsDocsSection() {
    return (
      shipment.needsCustomsDocs &&
      (shipment.shipmentState === ShipmentState.BookingConfirmed ||
        shipment.shipmentState === ShipmentState.OnHold ||
        shipment.shipmentState === ShipmentState.InTransit)
    );
  }

  return (
    <>
      {mapForShipment(shipment)}
      <Stack gap={8} align="center">
        <TrackingOverlapCard>
          <Stack gap={16}>
            {topBarForShipment(shipment)}
            <Stack gap={0} align="start">
              {showCustomsDocsSection() &&
                customsDocsSectionForShipment(shipment, props.onReloadShipment)}
              <NextStepsSectionForShipment_ shipment={shipment} />
              <NextStepsForDeliverySectionForShipment_ shipment={shipment} />
              <DetailsSectionForShipment_
                shipment={shipment}
                trackingInfo={trackingInfo}
              />
              <TrackingSectionForShipment_
                shipment={shipment}
                trackingInfo={trackingInfo}
              />
              <DocumentsSectionForShipment_ shipment={shipment} />
            </Stack>
          </Stack>
        </TrackingOverlapCard>
        <Stack gap={4} align="center">
          <T size="2xl" weight="light" color="muted">
            Want your next shipment to be this easy?
          </T>
          <Button
            primary
            size="large"
            onClick={() => {
              window.open("https://www.freightsimple.com/", "_blank");
            }}
          >
            Book it on FreightSimple!
          </Button>
        </Stack>
      </Stack>
    </>
  );
}

function TrackOneShipment_(props: { overrideTrackingId?: string }) {
  const query = useQuery();

  const trackingId = trackingResolveTrackingId(props.overrideTrackingId, query);

  const trackingService = useTrackingService();

  const [shipment, setShipment] = useState<ShipmentForTracking | undefined>();
  const [loading, setLoading] = useState(false);
  const [errorStatusCode, setErrorStatusCode] = useState<number>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);

  const [trackingDetails, setTrackingDetails] = useState<GetTrackingDetails>();

  const { switchToLinkedCompany } = useUserService();

  const { isAuthenticated, user } = useAuthentication();

  const navigate = useNavigate();

  async function loadShipment() {
    // Without a tracking id in the url there is nothing to load
    if (trackingId === undefined) {
      return;
    }
    try {
      setLoading(() => true);
      setErrorStatusCode(() => undefined);
      setLoadFailed(() => false);

      const details = await trackingService.getTrackingDetails(trackingId);
      setTrackingDetails(details);

      let switchFailed = false;
      if (details.companyId && details.companyId !== user?.companyId) {
        setSwitchLoading(() => true);
        const switchResult = await switchToLinkedCompany(details.companyId);
        switchFailed = !switchResult.success;

        if (!switchFailed) {
          // Wait long enough for the user to read the message
          await new Promise((resolve) => setTimeout(resolve, 2_500));
        }
      }

      const nextStep = trackingResolveNextStep(details, switchFailed);
      switch (nextStep.kind) {
        case "redirect":
          navigate(trackingResolveRedirectPath(nextStep.state, nextStep.id), {
            replace: true,
          });
          return;
        case "notFound":
          setErrorStatusCode(404);
          return;
        case "showPublicTracking":
          await loadShipmentUnauthenticated();
          return;
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        setErrorStatusCode(404);
      } else {
        console.warn("Error loading tracking:", { error });
        setLoadFailed(true);
      }
    } finally {
      setSwitchLoading(() => false);
      setLoading(() => false);
    }
  }

  async function loadShipmentUnauthenticated() {
    // Only called from loadShipment, which already checked trackingId
    if (trackingId === undefined) {
      return;
    }
    // A failure here is handled by loadShipment's catch
    const response = await trackingService.getShipmentForTracking(trackingId);
    setShipment(response);
  }

  useEffect(() => {
    loadShipment();
  }, [isAuthenticated]);

  // Refresh the page every ten minutes
  useInterval(
    function () {
      loadShipment();
    },
    10 * 60 * 1000,
  );

  if (switchLoading && trackingDetails?.companyName) {
    return (
      <Center minHeight="calc(100vh - 140px)">
        <Stack gap={0} align="center">
          <Spinner size={48} />
          <Heading level={1} size="3xl">
            Accessing linked organization data
          </Heading>
          <T size="base" color="secondary" align="center">
            We’re switching you to{" "}
            <strong>{trackingDetails?.companyName}</strong> to give you access
            to its shipments and related information. This will only take a
            moment.
          </T>
        </Stack>
      </Center>
    );
  }

  if (
    !isAuthenticated &&
    trackingDetails?.state === TrackingShipmentState.Draft
  ) {
    return (
      <Stack gap={5} align="center">
        <Stack gap={0} align="center">
          <Heading level={1} size="3xl">
            Unfortunately, you can't access this page
          </Heading>
          <T size="base" color="secondary" align="center">
            It looks like you're trying to view a draft. Please sign in to
            continue.
          </T>
        </Stack>
        <Row gap={6}>
          <Button size="link" href="/sign-in">
            Sign in
          </Button>
          <Button size="link" href="/book/details">
            Get Quotes for a Different Shipment
          </Button>
        </Row>
      </Stack>
    );
  }

  if (errorStatusCode === 404 && !loading) {
    return (
      <Center minHeight="calc(100vh - 140px)">
        <Stack gap={5} align="center">
          <Stack gap={0} align="center">
            <Heading level={2}>Shipment Not Found</Heading>
            <T size="base" color="secondary" align="center">
              Please verify the tracking link and try again.
            </T>
          </Stack>
          <ErrorAnimation />
        </Stack>
      </Center>
    );
  }

  // A failed ten-minute refresh keeps showing the shipment already on screen
  if (loadFailed && !shipment && !loading) {
    return <LoadFailed onRetry={loadShipment} />;
  }

  if (!shipment || loading) {
    return <PageLoadingSpinner />;
  }

  return <ShowShipment_ shipment={shipment} onReloadShipment={loadShipment} />;
}

function TrackShipmentScreen() {
  const query = useQuery();

  const choice = trackingResolveGroup(
    query["tId"] ?? query["trackingId"],
    query["d"],
  );

  if (choice.kind === "showGroup") {
    return <TrackingShipmentsScreen packedGroup={choice.packedGroup} />;
  }

  if (choice.kind === "showOneShipment") {
    return <TrackOneShipment_ overrideTrackingId={choice.trackingId} />;
  }

  return <TrackOneShipment_ />;
}
export default TrackShipmentScreen;
